import { createPersistentStore } from './utilsStore';

// Clear the existing store to start fresh
if (typeof window !== 'undefined') {
  localStorage.removeItem('memberData');
}

// Re-export Member type from backend for convenience
export type { Member } from '@vivport/backend/types';

interface MemberUIState {
  selectedMemberIds: number[];
  hasOpenedSpeak2MeDesktop: boolean;
  selectMember: (memberId: number) => void;
  deselectMember: (memberId: number) => void;
  toggleMemberSelection: (memberId: number) => void;
  selectAllMembers: (allMemberIds: number[]) => void;
  deselectAllMembers: () => void;
  areAllMembersSelected: (totalMembers: number) => boolean;
  hasSelectedMembers: () => boolean;
  setHasOpenedSpeak2MeDesktop: (hasOpened: boolean) => void;
}

// Helper function to ensure selectedMemberIds is always an array
const ensureArray = (value: any): number[] => {
  if (Array.isArray(value)) {
    return value;
  }
  return [];
};

export const useMemberStore = createPersistentStore<MemberUIState>(
  'memberData', 
  (set, get) => {
    return {
      selectedMemberIds: [],
      hasOpenedSpeak2MeDesktop: false,

      selectMember: (memberId: number) => {
        set(state => {
          const currentIds = ensureArray(state.selectedMemberIds);
          if (currentIds.includes(memberId)) {
            return state; // Already selected
          }
          return { 
            ...state, 
            selectedMemberIds: [...currentIds, memberId] 
          };
        });
      },

      deselectMember: (memberId: number) => {
        set(state => {
          const currentIds = ensureArray(state.selectedMemberIds);
          return {
            ...state,
            selectedMemberIds: currentIds.filter(id => id !== memberId)
          };
        });
      },

      toggleMemberSelection: (memberId: number) => {
        set(state => {
          const currentIds = ensureArray(state.selectedMemberIds);
          if (currentIds.includes(memberId)) {
            return {
              ...state,
              selectedMemberIds: currentIds.filter(id => id !== memberId)
            };
          } else {
            return {
              ...state,
              selectedMemberIds: [...currentIds, memberId]
            };
          }
        });
      },

      selectAllMembers: (allMemberIds: number[]) => {
        set(state => ({
          ...state,
          selectedMemberIds: allMemberIds
        }));
      },

      deselectAllMembers: () => {
        set(state => ({
          ...state,
          selectedMemberIds: []
        }));
      },

      areAllMembersSelected: (totalMembers: number) => {
        const state = get();
        const selectedIds = ensureArray(state.selectedMemberIds);
        return totalMembers > 0 && selectedIds.length === totalMembers;
      },

      hasSelectedMembers: () => {
        const selectedIds = ensureArray(get().selectedMemberIds);
        return selectedIds.length > 0;
      },

      setHasOpenedSpeak2MeDesktop: (hasOpenedSpeak2MeDesktop: boolean) => {
        set(state => ({
          ...state,
          hasOpenedSpeak2MeDesktop
        }));
      },
    };
  }
);
